import React, { useState } from "react";
import { User, Users, CreditCard, ShieldCheck, ShieldAlert, BarChart3 } from "lucide-react";
import GeneralSettings from "../components/composite/GeneralSettings";
import RepGuardTeamManagement from "../components/features/repguard/RepGuardTeamManagement";
import RepCheckBillingAdmin from "../components/features/repguard/RepCheckBillingAdmin";
import RepGuardPolicyAdmin from "../components/features/repguard/RepGuardPolicyAdmin";
import RepGuardReviewQueue from "../components/features/repguard/RepGuardReviewQueue";
import RepGuardAnalyticsHistory from "../components/features/repguard/RepGuardAnalyticsHistory";
import usePageMeta from "../hooks/usePageMeta";

/**
 * Settings - the customer's own account.
 *
 * Everything here operates on the signed-in user's workspace via /api/users/*
 * and /api/extension/* (all `requireAuth`, no platform permission), which is
 * precisely why it does NOT belong on the staff Admin console where Team,
 * Billing and RepGuard previously lived. A customer managing their own team or
 * their own subscription is not an administrator of RepCheck.
 */

const GROUPS = [
  {
    label: "Account",
    items: [
      { id: "general", label: "Profile & preferences", icon: User, render: () => <GeneralSettings /> },
      { id: "team", label: "Team", icon: Users, render: () => <RepGuardTeamManagement /> },
      { id: "billing", label: "Plan & usage", icon: CreditCard, render: () => <RepCheckBillingAdmin /> },
    ],
  },
  {
    label: "RepGuard",
    items: [
      { id: "rg-policy", label: "Policy", icon: ShieldCheck, render: () => <RepGuardPolicyAdmin /> },
      { id: "rg-reviews", label: "Draft reviews", icon: ShieldAlert, render: () => <RepGuardReviewQueue /> },
      { id: "rg-analytics", label: "Analytics", icon: BarChart3, render: () => <RepGuardAnalyticsHistory /> },
    ],
  },
];

const ALL = GROUPS.flatMap((g) => g.items);

export default function SettingsPage() {
  const [active, setActive] = useState("general");
  usePageMeta({ title: "Settings · RepCheck" });

  const current = ALL.find((i) => i.id === active) || ALL[0];

  return (
    <div className="h-full flex flex-col">
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Settings</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Your profile, your team, your plan, and how RepGuard checks your drafts.
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-muted">
        <div className="max-w-7xl mx-auto p-4 sm:p-6">
          {/* Mobile: one grouped select beats a rail that eats the screen */}
          <div className="md:hidden mb-4">
            <label htmlFor="settings-section" className="sr-only">Settings section</label>
            <select
              id="settings-section"
              value={active}
              onChange={(e) => setActive(e.target.value)}
              className="w-full rounded-lg border border-input bg-card px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            >
              {GROUPS.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.items.map((i) => (
                    <option key={i.id} value={i.id}>{i.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div className="flex gap-6">
            {/* Desktop rail */}
            <nav className="hidden md:block w-56 shrink-0">
              {GROUPS.map((g) => (
                <div key={g.label} className="mb-6">
                  <p className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {g.label}
                  </p>
                  <div className="space-y-0.5">
                    {g.items.map((i) => {
                      const Icon = i.icon;
                      const isActive = active === i.id;
                      return (
                        <button
                          key={i.id}
                          onClick={() => setActive(i.id)}
                          className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                            isActive
                              ? "bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-200"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          }`}
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          <span className="truncate">{i.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>

            <div className="min-w-0 flex-1">
              <div className="rounded-xl border border-border bg-card p-4 shadow-card sm:p-6">
                {current.render()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
